import { ui } from '@libs/domain';
import { colors } from '@libs/ui';
// Deep icon import (not the `@mui/icons-material` barrel), which trips EMFILE under vitest.
import Search from '@mui/icons-material/Search';
import { Box, InputAdornment, List, TextField } from '@mui/material';
import { useItemNames } from '@ps-smart-cp/ppfw-client';
import type { Space } from '../../../models';
import { useSpaceOptions } from '../hooks';
import { CreateSpaceFooterItem } from './CreateSpaceFooterItem';
import { SpaceMenuItem } from './SpaceMenuItem';

/**
 * 検索ボックス + 台帳一覧 + 「新規台帳を作成」行の選択 UI。
 *
 * <p>このコンポーネントは副作用を持たない。台帳切替・作成 API の呼び出しは行わず、選択と
 * 作成を {@link SpaceSelectListProps.onSelect} / {@link SpaceSelectListProps.onCreate} で
 * 親に通知するだけ。入力があれば、一致の有無に関わらず作成行を出す。
 */

interface SpaceSelectListProps {
  /** 現在選択中の台帳の Space code（チェックマーク表示に使う）。 */
  spaceCode: string;
  /** 選択候補。 */
  spaceOptions: Space[];
  /** 台帳が選ばれたときの通知。実際の切替は呼び出し側が行う。 */
  onSelect: (space: Space) => void;
  /** 作成行が押されたときの通知。実際の作成は呼び出し側が行う。 */
  onCreate: (name: string) => void;
}

function SpaceSelectList(props: SpaceSelectListProps) {
  const { spaceCode, spaceOptions, onSelect, onCreate } = props;
  const { filteredOptions, searchText, setSearchText } = useSpaceOptions(spaceOptions);
  const getLabelName = useItemNames(ui.labelNames);

  return (
    <>
      <Box
        sx={{
          p: 1,
          width: '100%',
          boxSizing: 'border-box',
          bgcolor: 'background.paper',
          position: 'sticky',
          top: 0,
          zIndex: 1,
        }}
      >
        <TextField
          size="small"
          placeholder={getLabelName('searchSpacePlaceholder')}
          fullWidth
          value={searchText}
          slotProps={{
            htmlInput: { maxLength: 40 },
            input: {
              sx: { fontSize: '12px' },
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" sx={{ color: colors.deepGray }} />
                </InputAdornment>
              ),
            },
          }}
          onChange={(e) => setSearchText(e.target.value)}
          onKeyDown={(e) => {
            e.stopPropagation();
          }}
        />
      </Box>
      <List disablePadding>
        {filteredOptions.map((option) => (
          <SpaceMenuItem
            key={option.spaceCode}
            space={option}
            isSelected={option.spaceCode === spaceCode}
            onClick={() => onSelect(option)}
          />
        ))}
      </List>
      <CreateSpaceFooterItem name={searchText} onCreate={onCreate} />
    </>
  );
}

export { SpaceSelectList };
